import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import logo from '../assets/lemon_tree_transparent.png';

const C = {
  cream: '#F7F6EF', charcoal: '#2F3A42', navy: '#445366',
  mid: '#7A8A96', border: '#D8DEE3', white: '#FFFFFF',
  red: '#DC3545', redLight: '#FFF0F0',
  green: '#7EA594', greenLight: '#EAF2EF',
};

export default function SetPassword() {
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [status,    setStatus]    = useState('loading'); // loading | ready | saving | done | invalid
  const [errorMsg,  setErrorMsg]  = useState('');
  const [tokenType, setTokenType] = useState('invite');

  useEffect(() => {
    // Supabase fires onAuthStateChange with event PASSWORD_RECOVERY or SIGNED_IN
    // when the user lands via an invite or recovery link.
    // We listen here and hold the user on this page until they set a password.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setTokenType('recovery');
        setStatus('ready');
      } else if (event === 'SIGNED_IN') {
        // Invite links fire SIGNED_IN — check the URL hash to confirm it's an invite
        const hash   = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const type   = params.get('type');
        if (type === 'invite' || type === 'recovery') {
          setTokenType(type);
          setStatus('ready');
        }
        // If SIGNED_IN but no invite/recovery type in hash, let App.js handle it normally
      }
    });

    // Also check hash immediately in case event already fired before listener attached
    const hash   = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const type   = params.get('type');
    const token  = params.get('access_token');

    if (token && (type === 'invite' || type === 'recovery')) {
      setTokenType(type);
      // Set session from URL tokens so Supabase can process them
      supabase.auth.setSession({
        access_token:  token,
        refresh_token: params.get('refresh_token') || '',
      }).then(({ error }) => {
        if (error) {
          setStatus('invalid');
        } else {
          setStatus('ready');
        }
      });
    } else {
      // No valid token in hash — check if we already have a session
      // (can happen if user navigates back to /set-password after setting password)
      supabase.auth.getSession().then(({ data }) => {
        if (!data.session) setStatus('invalid');
        // If session exists but no token type, show invalid — don't allow
        // re-using this page as a generic password change form
        else setStatus('invalid');
      });
    }

    return () => subscription.unsubscribe();
  }, []);

  const weak     = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;
  const ready    = password.length >= 8 && password === confirm;

  async function handleSubmit() {
    if (!ready) return;
    setStatus('saving');
    setErrorMsg('');

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      setErrorMsg(error.message || 'Something went wrong. Try requesting a new link.');
      setStatus('ready');
      return;
    }

    setStatus('done');

    // Sign out and redirect to login so they log in fresh with their new password
    setTimeout(async () => {
      await supabase.auth.signOut();
      window.location.href = '/';
    }, 2000);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSubmit();
  }

  // ── Loading ───────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <Shell>
        <div style={{ textAlign: 'center', color: C.mid, fontSize: '0.9rem' }}>
          Verifying link…
        </div>
      </Shell>
    );
  }

  // ── Invalid / expired ─────────────────────────────────────────────────
  if (status === 'invalid') {
    return (
      <Shell>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🍋</div>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: C.charcoal }}>Invalid or expired link</h2>
          <p style={{ margin: '0 0 24px', fontSize: '0.875rem', color: C.mid, lineHeight: 1.6 }}>
            This link has already been used or has expired.<br/>
            Ask your admin to resend the invite, or use<br/>
            "Forgot password?" on the login page.
          </p>
          <a href="/" style={{ display: 'inline-block', padding: '10px 24px', background: C.navy, color: C.white, borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem' }}>
            Back to Login
          </a>
        </div>
      </Shell>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────
  if (status === 'done') {
    return (
      <Shell>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: C.greenLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <span style={{ fontSize: '24px' }}>✓</span>
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: C.charcoal }}>Password set!</h2>
          <p style={{ margin: 0, fontSize: '0.875rem', color: C.mid }}>Taking you to login…</p>
        </div>
      </Shell>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────
  const isInvite = tokenType === 'invite';

  return (
    <Shell>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <img src={logo} alt="Lemon Tree" style={{ height: '56px', marginBottom: '12px' }} />
        <h2 style={{ margin: '0 0 4px', fontSize: '1.2rem', color: C.charcoal }}>
          {isInvite ? 'Set your password' : 'Choose a new password'}
        </h2>
        <p style={{ margin: 0, fontSize: '0.82rem', color: C.mid }}>
          {isInvite
            ? 'Welcome to Lemon Tree — create a password to get started.'
            : 'Enter a new password for your account.'}
        </p>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', fontSize: '0.82rem', color: '#555', marginBottom: '4px', fontWeight: 500 }}>
          New Password
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Min. 8 characters"
            autoFocus
            style={{ width: '100%', padding: '0.65rem 2.5rem 0.65rem 0.75rem', border: `1px solid ${weak ? C.red : C.border}`, borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box', outline: 'none' }}
          />
          <button
            onClick={() => setShowPw(p => !p)}
            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.mid, fontSize: '12px', padding: '2px' }}>
            {showPw ? 'Hide' : 'Show'}
          </button>
        </div>
        {weak && <div style={{ fontSize: '11px', color: C.red, marginTop: '4px' }}>At least 8 characters required</div>}
      </div>

      <div style={{ marginBottom: '1.25rem' }}>
        <label style={{ display: 'block', fontSize: '0.82rem', color: '#555', marginBottom: '4px', fontWeight: 500 }}>
          Confirm Password
        </label>
        <input
          type={showPw ? 'text' : 'password'}
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Repeat password"
          style={{ width: '100%', padding: '0.65rem 0.75rem', border: `1px solid ${mismatch ? C.red : C.border}`, borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box', outline: 'none' }}
        />
        {mismatch && <div style={{ fontSize: '11px', color: C.red, marginTop: '4px' }}>Passwords don't match</div>}
      </div>

      {errorMsg && (
        <div style={{ background: C.redLight, border: `1px solid ${C.red}44`, borderRadius: '6px', padding: '10px 12px', marginBottom: '1rem', fontSize: '0.82rem', color: C.red }}>
          {errorMsg}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={!ready || status === 'saving'}
        style={{ width: '100%', padding: '0.75rem', background: ready ? C.navy : '#ccc', color: C.white, border: 'none', borderRadius: '6px', cursor: ready ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: '1rem' }}>
        {status === 'saving' ? 'Setting password…' : 'Set Password'}
      </button>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '2.5rem', width: '100%', maxWidth: '380px' }}>
        {children}
      </div>
    </div>
  );
}